import type { AnyValue } from "@/lib/rowTypes";
import { musicController } from "./musicController";
import { supabase } from "@/integrations/supabase/client";
import { getLessonMoment, type LessonVoiceMoment } from "./lessonVoiceMap";
import { getLumiHolocardMoment } from "./lumiHolocardVoiceMap";
import { isApprovedInstructor, approvedVoiceIdForInstructor } from "./approvedVoices";
import { getErrorMessage } from "@/lib/errors";

/**
 * Internal resolver — a "voice moment" the lesson controller can play is
 * either an auto-fire lesson moment or a click-to-play Lumi holocard.
 * Both route through the same `instructor-voice` edge function.
 */
function resolveMomentForController(momentId: string): LessonVoiceMoment | null {
  const lesson = getLessonMoment(momentId);
  if (lesson) return lesson;
  const holo = getLumiHolocardMoment(momentId);
  if (!holo) return null;
  return {
    lessonId: holo.lessonId,
    lessonSlug: holo.lessonSlug,
    stage: (holo.stage === "infographic" ? "post_video"
      : holo.stage === "post_decision" ? "post_decision"
      : "pre_decision") as LessonVoiceMoment["stage"],
    momentId: holo.momentId,
    instructor: "lumi",
    transcript: holo.transcript,
    estimatedDurationSeconds: 8,
  };
}

/**
 * Lesson voice-moment controller.
 *
 * Parallel to `instructorVoiceController` but with its own play-once set,
 * speaking-state listeners, and URL cache so lesson captions stay cleanly
 * separate from module instructor captions. Reuses the existing
 * `instructor-voice` edge function (which gates on a server-side allowlist
 * that mirrors `lessonVoiceMap`).
 */

export type LessonVoicePlayStatus =
  | "played"
  | "suppressed"
  | "already_played"
  | "queued_autoplay"
  | "generating_timeout"
  | "unavailable"
  | "failed"
  | "unknown_moment"
  | "blocked_unapproved_voice";

export interface LessonVoicePlayResult {
  momentId: string;
  status: LessonVoicePlayStatus;
  backendStatus?: string;
  cached?: boolean;
  autoplayBlocked?: boolean;
}

interface VoiceUrlResolution {
  url: string | null;
  status: string;
  cached: boolean;
}

const POLL_INTERVAL_MS = 900;
const MAX_READY_WAIT_MS = 10_800;
const POST_SFX_DELAY_MS = 250;

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

class LessonVoiceController {
  private played = new Set<string>();
  private inflight = new Map<string, Promise<VoiceUrlResolution>>();
  private playAttempts = new Map<string, Promise<LessonVoicePlayResult>>();
  private urlCache = new Map<string, string>();
  private queuedAutoplay = new Set<string>();
  private current: HTMLAudioElement | null = null;
  private releaseDuck: ((ms?: number) => void) | null = null;
  private currentMomentId: string | null = null;
  private gestureListenersReady = false;

  private enabled = true;
  private volume = 0.9;
  private muted = false;

  private listeners = new Set<() => void>();

  setEnabled(v: boolean) { this.enabled = v; if (!v) this.stop(); }
  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.current) try { this.current.volume = this.volume; } catch { /* ignore */ }
  }
  setMuted(m: boolean) { this.muted = m; if (m) this.stop(); }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }
  private notify() { this.listeners.forEach((f) => { try { f(); } catch { /* ignore */ } }); }

  getSpeakingMomentId(): string | null { return this.currentMomentId; }

  /** Clear fire-once tracking. Call on logout/lesson reset for QA. */
  reset() { this.played.clear(); this.queuedAutoplay.clear(); }

  /** Forget that a single momentId was already played (lesson change). */
  resetMoments(momentIds: string[]) {
    for (const id of momentIds) {
      this.played.delete(id);
      this.queuedAutoplay.delete(id);
    }
  }

  private ensureGestureListeners() {
    if (this.gestureListenersReady || typeof window === "undefined") return;
    this.gestureListenersReady = true;
    const flush = () => { void this.flushQueuedAutoplay(); };
    window.addEventListener("pointerdown", flush, { capture: true, passive: true });
    window.addEventListener("touchend", flush, { capture: true, passive: true });
    window.addEventListener("keydown", flush, { capture: true });
    window.addEventListener("click", flush, { capture: true, passive: true });
  }

  private async flushQueuedAutoplay() {
    if (!this.queuedAutoplay.size) return;
    const pending = Array.from(this.queuedAutoplay);
    this.queuedAutoplay.clear();
    for (const momentId of pending) {
      void this.play(momentId, { fromAutoplayQueue: true, skipDelay: true });
    }
  }

  async preload(momentIds: string[]): Promise<void> {
    this.ensureGestureListeners();
    const moments = momentIds
      .map((id) => resolveMomentForController(id))
      .filter((m): m is LessonVoiceMoment => !!m);
    let i = 0;
    const workerCount = Math.min(2, moments.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (i < moments.length) {
        const m = moments[i++];
        await this.resolveUrl(m, { poll: false, preload: true }).catch(() => null);
      }
    });
    await Promise.all(workers);
  }

  stop() {
    if (this.current) {
      try { this.current.pause(); this.current.currentTime = 0; } catch { /* ignore */ }
      this.current = null;
    }
    if (this.releaseDuck) { this.releaseDuck(300); this.releaseDuck = null; }
    if (this.currentMomentId) {
      this.currentMomentId = null;
      this.notify();
    }
  }

  async play(
    momentId: string,
    options: { fromAutoplayQueue?: boolean; skipDelay?: boolean } = {},
  ): Promise<LessonVoicePlayResult> {
    this.ensureGestureListeners();
    const moment = resolveMomentForController(momentId);
    if (!moment) return { momentId, status: "unknown_moment" };
    // Defense-in-depth: refuse to play for an instructor slug that does
    // not map to an approved ElevenLabs voice ID. Does not add momentId
    // to `played` so no "played" telemetry is emitted for rejected voices.
    if (!isApprovedInstructor(moment.instructor)) {
      console.warn("[lesson-voice] blocked unapproved voice for instructor", {
        momentId,
        instructor: moment.instructor,
        resolvedVoiceId: approvedVoiceIdForInstructor(moment.instructor),
      });
      return { momentId, status: "blocked_unapproved_voice" };
    }
    if (!this.enabled || this.muted) {
      this.played.add(momentId);
      return { momentId, status: "suppressed" };
    }
    if (this.played.has(momentId)) return { momentId, status: "already_played" };

    const existing = this.playAttempts.get(momentId);
    if (existing) return existing;

    const attempt = this.playInternal(moment, options).finally(() => {
      this.playAttempts.delete(momentId);
    });
    this.playAttempts.set(momentId, attempt);
    return attempt;
  }

  private async playInternal(
    moment: LessonVoiceMoment,
    options: { fromAutoplayQueue?: boolean; skipDelay?: boolean },
  ): Promise<LessonVoicePlayResult> {
    const momentId = moment.momentId;

    if (!options.skipDelay) await wait(POST_SFX_DELAY_MS);
    if (!this.enabled || this.muted) {
      this.played.add(momentId);
      return { momentId, status: "suppressed" };
    }

    const resolved = await this.resolveUrl(moment, { poll: true, preload: false });
    if (!resolved.url) {
      const timedOut = resolved.status === "generating" || resolved.status === "generating_timeout";
      return {
        momentId,
        status: timedOut ? "generating_timeout" : "unavailable",
        backendStatus: resolved.status,
        cached: resolved.cached,
      };
    }
    if (!this.enabled || this.muted) {
      this.played.add(momentId);
      return { momentId, status: "suppressed", backendStatus: resolved.status };
    }

    this.stop();

    let audio: HTMLAudioElement;
    try {
      audio = new Audio(resolved.url);
      audio.volume = this.volume;
      audio.preload = "auto";
    } catch {
      return { momentId, status: "failed", backendStatus: resolved.status, cached: resolved.cached };
    }

    this.current = audio;
    this.currentMomentId = momentId;
    this.releaseDuck = musicController.pushDuck(0.45, 250);
    this.notify();

    const release = () => {
      if (this.current === audio) {
        this.current = null;
        this.currentMomentId = null;
        if (this.releaseDuck) { this.releaseDuck(400); this.releaseDuck = null; }
        this.notify();
      }
    };
    audio.addEventListener("ended", release);
    audio.addEventListener("error", release);
    try {
      const p = audio.play();
      if (p && typeof p.then === "function") await p;
      this.played.add(momentId);
      return { momentId, status: "played", backendStatus: resolved.status, cached: resolved.cached };
    } catch (e: unknown) {
      const message = String(getErrorMessage(e) ?? e ?? "");
      const name = String((e as AnyValue)?.name ?? "");
      const autoplayBlocked =
        name === "NotAllowedError" ||
        /autoplay|user activation|gesture|permission/i.test(message);
      release();
      if (autoplayBlocked) {
        this.queuedAutoplay.add(momentId);
        return {
          momentId,
          status: "queued_autoplay",
          backendStatus: resolved.status,
          cached: resolved.cached,
          autoplayBlocked: true,
        };
      }
      return { momentId, status: "failed", backendStatus: resolved.status, cached: resolved.cached };
    }
  }

  private async resolveUrl(
    moment: LessonVoiceMoment,
    options: { poll: boolean; preload: boolean },
  ): Promise<VoiceUrlResolution> {
    const key = `lesson:${moment.momentId}:${moment.instructor}:${moment.transcript}`;
    const cached = this.urlCache.get(key);
    if (cached) return { url: cached, status: "ready", cached: true };

    const deadline = Date.now() + MAX_READY_WAIT_MS;
    let last: VoiceUrlResolution = { url: null, status: "missing", cached: false };
    do {
      const existing = this.inflight.get(key);
      const result = existing ?? this.requestSignedUrl(moment, key, options.preload);
      if (!existing) this.inflight.set(key, result);
      last = await result;
      if (last.url) return last;
      if (!options.poll) return last;
      if (last.status !== "generating") return last;
      if (Date.now() >= deadline) {
        return { ...last, status: "generating_timeout" };
      }
      await wait(POLL_INTERVAL_MS);
    } while (Date.now() < deadline);
    return { ...last, status: last.status === "generating" ? "generating_timeout" : last.status };
  }

  private async requestSignedUrl(
    moment: LessonVoiceMoment,
    key: string,
    preload: boolean,
  ): Promise<VoiceUrlResolution> {
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "playback", moment_id: moment.momentId, preload },
      });
      if (error) return { url: null, status: "network_error", cached: false };
      const body = data;
      const url = body?.url as string | undefined;
      const status = (body?.status as string | undefined) ?? (url ? "ready" : "missing");
      const cached = Boolean(body?.cached);
      if (url) {
        this.urlCache.set(key, url);
        return { url, status: "ready", cached };
      }
      return { url: null, status, cached };
    } catch {
      return { url: null, status: "network_error", cached: false };
    } finally {
      try { this.inflight.delete(key); } catch { /* ignore */ }
    }
  }
}

export const lessonVoice = new LessonVoiceController();
export type { LessonVoiceMoment };